import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { RootState } from "../../../redux/store";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../redux/loader/loaderActions";
import baseUrl from "../../../api/baseUrl";
import FormDate from "../../../components/formFeild/FormDate";
import BottomTab from "../../../components/pagination/BottomTab";
import AllPaymentReport from "../payments/allpayments/AllPaymentReport";
import CommonLayout from "../../../layouts/commonLayout/CommonLayout";
import LinkedAppointmentChip from "../../../components/LinkedAppointmentChip";
import { toColomboYmd } from "../../../utils/colomboDate";

interface FormData {
  startDate: string;
  endDate: string;
}

function paymentRowDate(item: any): string {
  return (
    toColomboYmd(item?.paymentDate || item?.businessDate || item?.createdAt) ||
    "—"
  );
}

function toAmount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatAmount(value: unknown): string {
  const n = toAmount(value);
  return n.toLocaleString();
}

function getPaidTotal(item: { cash?: unknown; card?: unknown; bankTransfer?: unknown }) {
  return toAmount(item?.cash) + toAmount(item?.card) + toAmount(item?.bankTransfer);
}

type DentistOption = {
  _id: string;
  title?: string;
  lastName?: string;
};

function dentistLabel(d: DentistOption): string {
  return [d.title, d.lastName].filter(Boolean).join(" ").trim() || "Dentist";
}

function todayDateString(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Colombo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

type BalanceStatus = "all" | "due" | "advanced" | "complete";

const BALANCE_STATUS_OPTIONS: { value: BalanceStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "due", label: "Due" },
  { value: "advanced", label: "Advanced" },
  { value: "complete", label: "Complete" },
];

function balanceStatusLabel(value: BalanceStatus): string {
  return BALANCE_STATUS_OPTIONS.find((o) => o.value === value)?.label ?? "All";
}

function weekdayFromIso(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date();
  return [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ][d.getDay()];
}

function mapSessionOptions(raw: unknown): {
  sessionName: string;
  startTime?: string;
  endTime?: string;
}[] {
  if (!Array.isArray(raw)) return [];
  const byName = new Map<
    string,
    { sessionName: string; startTime?: string; endTime?: string }
  >();
  for (const item of raw) {
    const o = item as Record<string, unknown>;
    const sessionName = String(o?.sessionName ?? "").trim();
    if (!sessionName) continue;
    const key = sessionName.toLowerCase();
    if (byName.has(key)) continue;
    byName.set(key, {
      sessionName,
      startTime: String(o?.startTime ?? "").trim(),
      endTime: String(o?.endTime ?? "").trim(),
    });
  }
  return Array.from(byName.values());
}

function paymentMatchesStatus(item: any, status: BalanceStatus): boolean {
  if (status === "all") return true;
  const due = toAmount(item?.due);
  if (status === "due") return due > 0;
  if (status === "advanced") return due < 0;
  return Math.abs(due) < 0.01;
}

function AllPaymentList() {
  const navigate = useNavigate();
  const [report, setreport] = useState(false);
  const [payment, setpayment] = useState([]);
  const [count, setCount] = useState(0);
  const [outstandingSum, setOutstandingSum] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterDoctorId, setFilterDoctorId] = useState<string>("all");
  const [dentists, setDentists] = useState<DentistOption[]>([]);
  const [filterSession, setFilterSession] = useState<string>("all");
  const [filterBalance, setFilterBalance] = useState<BalanceStatus>("all");
  const [sessions, setSessions] = useState<
    { sessionName: string; startTime?: string; endTime?: string }[]
  >([]);
  const [dateRangeMode, setDateRangeMode] = useState<"today" | "all">("today");
  const sessionTouchedRef = useRef(false);
  const search = useSelector((state: RootState) => state.searching.patientData);
  const methods = useForm<FormData>({
    defaultValues: {
      startDate: todayDateString(),
      endDate: todayDateString(),
    },
  });
  const { watch, reset, setValue } = methods;
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const dispatch = useDispatch();

  useEffect(() => {
    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setDentists(
          list
            .map((d: DentistOption) => ({
              _id: String(d?._id ?? ""),
              title: d?.title,
              lastName: d?.lastName,
            }))
            .filter((d: DentistOption) => d._id)
        );
      })
      .catch((error) => {
        console.error(error);
        setDentists([]);
      });
  }, []);

  useEffect(() => {
    const date =
      startDate && /^\d{4}-\d{2}-\d{2}$/.test(startDate)
        ? startDate
        : todayDateString();
    const weekday = weekdayFromIso(date);
    let cancelled = false;

    const apply = (
      list: { sessionName: string; startTime?: string; endTime?: string }[]
    ) => {
      if (cancelled) return;
      setSessions(list);
      setFilterSession((prev) => {
        if (sessionTouchedRef.current) {
          return prev === "all" || list.some((s) => s.sessionName === prev)
            ? prev
            : "all";
        }
        return prev;
      });
    };

    baseUrl
      .get("/client/setting/openHours/findSessionNames", {
        params: { date },
      })
      .then((res) => {
        const list = mapSessionOptions(res.data?.data?.sessions);
        if (list.length) {
          apply(list);
          return null;
        }
        return baseUrl.get("/client/setting/generalOpenHours/findGeneralOpenHours", {
          params: { day: weekday },
        });
      })
      .then((res) => {
        if (!res || cancelled) return;
        apply(mapSessionOptions(res.data?.data?.sessions));
      })
      .catch(() => {
        if (cancelled) return;
        baseUrl
          .get("/client/setting/generalOpenHours/findGeneralOpenHours", {
            params: { day: weekday },
          })
          .then((res) => apply(mapSessionOptions(res.data?.data?.sessions)))
          .catch(() => apply([]));
      });

    return () => {
      cancelled = true;
    };
  }, [startDate]);

  const getData = useCallback(
    (
      keyword: string,
      pageNum: number,
      startDate?: string,
      endDate?: string,
      doctorId?: string,
      sessionName?: string,
      options?: { disablePagination?: boolean; balanceStatus?: string }
    ) => {
      dispatch(setloadeActive());
      baseUrl
        .get("payment/getAllPayment-list", {
          headers: {
            keyword,
            page: options?.disablePagination ? 1 : pageNum,
            ...(options?.disablePagination ? { limit: "all" } : {}),
          },
          params: {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            doctorId:
              doctorId && doctorId !== "all" ? doctorId : undefined,
            sessionName:
              sessionName && sessionName !== "all" ? sessionName : undefined,
            balanceStatus:
              options?.balanceStatus && options.balanceStatus !== "all"
                ? options.balanceStatus
                : undefined,
          },
        })
        .then((response) => {
          dispatch(setloadeDeactive());
          setpayment(response.data.data);
          setCount(response.data.count);
          setOutstandingSum(Number(response.data?.outstandingSum) || 0);
        })
        .catch(() => {
          dispatch(setloadeDeactive());
        });
    },
    [dispatch]
  );

  const fetchPayments = useCallback(
    (
      keyword: string,
      pageNum: number,
      doctorId: string,
      from?: string,
      to?: string,
      sessionName?: string,
      balanceStatus?: string,
      disablePagination?: boolean
    ) => {
      getData(keyword, pageNum, from, to, doctorId, sessionName, {
        disablePagination,
        balanceStatus,
      });
    },
    [getData]
  );

  useEffect(() => {
    const activeSearchTerm = searchTerm || search || "";
    const from = startDate && endDate ? startDate : undefined;
    const to = startDate && endDate ? endDate : undefined;
    // Only Today loads all rows (no pager). Session/status/doctor use API filters + pagination.
    const loadAllRows = dateRangeMode === "today";
    fetchPayments(
      activeSearchTerm,
      loadAllRows ? 1 : page,
      filterDoctorId,
      from,
      to,
      filterSession,
      filterBalance,
      loadAllRows
    );
  }, [
    search,
    searchTerm,
    page,
    filterDoctorId,
    filterSession,
    filterBalance,
    dateRangeMode,
    startDate,
    endDate,
    fetchPayments,
  ]);

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    []
  );
  const closeModal = useCallback(() => {
    setreport(false);
  }, []);
  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      if (!data.startDate || !data.endDate) {
        toast.info("Select both From and To dates to download the report.");
        return;
      }
      const keyword = (searchTerm || search || "").trim();
      try {
        const response = await baseUrl.get("client/payment/payment-report", {
          responseType: "blob",
          params: {
            startDate: data.startDate,
            endDate: data.endDate,
            ...(filterDoctorId !== "all" ? { doctorId: filterDoctorId } : {}),
            ...(filterSession !== "all" ? { sessionName: filterSession } : {}),
            ...(filterBalance !== "all" ? { balanceStatus: filterBalance } : {}),
            ...(keyword ? { keyword } : {}),
          },
        });
        if (response.status === 204) {
          toast.info("No data found for the selected filters.");
          return;
        }
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(pdfBlob);
        link.download = `payment-report-${data.startDate}-to-${data.endDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      } catch (error: unknown) {
        const err = error as { response?: { status?: number } };
        if (err?.response?.status === 204) {
          toast.info("No data found for the selected filters.");
          return;
        }
        console.error("Error downloading the PDF", error);
        toast.error("Could not download the report.");
      }
    },
    [filterDoctorId, filterSession, filterBalance, search, searchTerm]
  );
  const methodReset = useCallback((e: any) => {
    e.preventDefault();
    reset();
    setSearchTerm("");
    setFilterDoctorId("all");
    sessionTouchedRef.current = false;
    setFilterSession("all");
    setFilterBalance("all");
    setDateRangeMode("today");
    setPage(1);
  }, [reset]);

  const applyDateRangeMode = useCallback(
    (mode: "today" | "all") => {
      setDateRangeMode(mode);
      setPage(1);
      if (mode === "today") {
        const todayStr = todayDateString();
        setValue("startDate", todayStr, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("endDate", todayStr, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }
      setValue("startDate", "", { shouldDirty: true });
      setValue("endDate", "", { shouldDirty: true });
    },
    [setValue]
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  const handleDoctorFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterDoctorId(e.target.value);
      setPage(1);
    },
    []
  );

  const handleSessionFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      sessionTouchedRef.current = true;
      setFilterSession(e.target.value);
      setPage(1);
    },
    []
  );

  const handleBalanceFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterBalance(e.target.value as BalanceStatus);
      setPage(1);
    },
    []
  );

  const displayedPayments = useMemo(() => {
    let rows = Array.isArray(payment) ? payment : [];
    // Session filter is applied by the API when sessionName is sent — don't
    // re-filter by clock here (keeps server and UI counts aligned).
    if (filterBalance !== "all") {
      rows = rows.filter((item) => paymentMatchesStatus(item, filterBalance));
    }
    return rows;
  }, [payment, filterBalance]);

  const filtersLoadAllRows = dateRangeMode === "today";
  // When paginated, show API total; Today shows filtered in-memory rows.
  const displayedCount = filtersLoadAllRows
    ? displayedPayments.length
    : count;

  const fieldLabelClass =
    "text-slate-700 dark:text-slate-300 text-[13px] font-medium leading-[1.4]";

  const cellClass = "px-2.5 py-1.5 text-xs";

  return (
    <CommonLayout>
      <div className="mt-4">
        <div className="rounded-[8px] border border-slate-100 bg-white p-3 shadow-lg shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-800 dark:shadow-slate-900/50">
          {/* Filter Form */}
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between"
            >
              <div className="flex flex-1 flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-end">
                <div className="flex w-full flex-col gap-1 sm:max-w-md">
                  <label className={fieldLabelClass}>Search</label>
                  <input
                    type="search"
                    placeholder="Invoice, patient, dentist..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all placeholder:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
                  />
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex w-36 shrink-0 flex-col gap-1">
                    <label className={fieldLabelClass}>Doctor</label>
                    <select
                      value={filterDoctorId}
                      onChange={handleDoctorFilter}
                      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <option value="all">All</option>
                      {dentists.map((d) => (
                        <option key={d._id} value={d._id}>
                          {dentistLabel(d)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex w-40 shrink-0 flex-col gap-1">
                    <label className={fieldLabelClass}>Session</label>
                    <select
                      value={filterSession}
                      onChange={handleSessionFilter}
                      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <option value="all">All Sessions</option>
                      {sessions.map((s) => (
                        <option key={s.sessionName} value={s.sessionName}>
                          {s.sessionName}
                          {s.startTime && s.endTime
                            ? ` (${s.startTime}-${s.endTime})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex w-36 shrink-0 flex-col gap-1">
                    <label className={fieldLabelClass}>Status</label>
                    <select
                      value={filterBalance}
                      onChange={handleBalanceFilter}
                      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    >
                      {BALANCE_STATUS_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {dateRangeMode !== "today" ? (
                    <>
                      <FormDate label="From" name="startDate" sameSize />
                      <FormDate label="To" name="endDate" sameSize />
                    </>
                  ) : null}
                  <div className="flex flex-col gap-1">
                    <span className={fieldLabelClass}>Period</span>
                    <div className="flex h-8 w-fit items-center gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-700">
                      {(
                        [
                          { value: "today", label: "Today" },
                          { value: "all", label: "All" },
                        ] as const
                      ).map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => applyDateRangeMode(value)}
                          className={`h-7 rounded-md px-2.5 text-xs font-medium transition-all ${
                            dateRangeMode === value
                              ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-600 dark:text-indigo-300"
                              : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="submit"
                  className="flex h-8 items-center whitespace-nowrap rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 text-xs font-medium text-white transition-all hover:shadow-md hover:shadow-primary/20"
                >
                  Download PDF
                </button>
                <button
                  type="button"
                  onClick={methodReset}
                  className="flex h-8 items-center whitespace-nowrap rounded-lg border border-slate-200 bg-slate-100 px-3 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                  Reset
                </button>
              </div>
            </form>
          </FormProvider>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-0.5">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                {displayedCount}
              </span>{" "}
              {displayedCount === 1 ? "record" : "records"}
              {dateRangeMode === "today" ? (
                <span className="text-slate-400 dark:text-slate-500"> · Today</span>
              ) : startDate && endDate ? (
                <span className="text-slate-400 dark:text-slate-500">
                  {" "}
                  · {startDate} to {endDate}
                </span>
              ) : (
                <span className="text-slate-400 dark:text-slate-500"> · All</span>
              )}
              {filterDoctorId !== "all" ? (
                <span className="text-slate-400 dark:text-slate-500"> · Doctor filtered</span>
              ) : null}
              {filterSession !== "all" ? (
                <span className="text-slate-400 dark:text-slate-500">
                  {" "}
                  · {filterSession}
                </span>
              ) : null}
              {filterBalance !== "all" ? (
                <span className="text-slate-400 dark:text-slate-500">
                  {" "}
                  · {balanceStatusLabel(filterBalance)}
                </span>
              ) : null}
              {outstandingSum > 0 ? (
                <span className="ml-1.5 font-semibold tabular-nums text-red-600 dark:text-red-400">
                  · Outstanding Rs {Number(outstandingSum).toLocaleString()}
                </span>
              ) : null}
            </p>
          </div>

          {/* Desktop Table */}
          <div className="mt-3 hidden overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-600 md:block">
            <table className="w-full min-w-[900px] table-fixed">
              <colgroup>
                <col style={{ width: "108px" }} />
                <col />
                <col />
                <col style={{ width: "72px" }} />
                <col style={{ width: "68px" }} />
                <col style={{ width: "68px" }} />
                <col style={{ width: "68px" }} />
                <col style={{ width: "68px" }} />
                <col style={{ width: "72px" }} />
                <col style={{ width: "80px" }} />
              </colgroup>
              <thead className="border-b border-slate-200 bg-slate-50/90 dark:border-slate-600 dark:bg-slate-700/60">
                <tr>
                  <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>Date / Invoice</th>
                  <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>Patient</th>
                  <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>Dentist</th>
                  <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>Total</th>
                  <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>Discount</th>
                  <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>Cash</th>
                  <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>Card</th>
                  <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>Bank</th>
                  <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>Paid</th>
                  <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>Due / Adv</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/80 bg-white dark:bg-slate-800">
                {displayedPayments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-2.5 py-6 text-center text-xs text-slate-500 dark:text-slate-400"
                    >
                      No payments found.
                    </td>
                  </tr>
                ) : (
                  displayedPayments.map((item: any, index) => {
                    const paidTotal = getPaidTotal(item);
                    const dueAmount = toAmount(item?.due);
                    const isDue = dueAmount > 0;

                    return (
                      <tr
                        key={item?._id ?? item?.treatmentId ?? index}
                        onClick={() => navigate(`/client/payments/invoice/${item?.treatmentId}`)}
                        className="group hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20 cursor-pointer transition-colors even:bg-slate-50/40 dark:even:bg-slate-800/60"
                      >
                        <td className={`${cellClass} whitespace-nowrap`}>
                          <div className="font-semibold text-indigo-700 dark:text-indigo-300">
                            #{item?.invoiceNo ?? "—"}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            {paymentRowDate(item)}
                          </div>
                          <LinkedAppointmentChip
                            appointment={item?.linkedAppointment}
                          />
                        </td>
                        <td className={`${cellClass} truncate font-semibold text-slate-900 dark:text-slate-100`} title={item?.patientName}>
                          {item?.patientName || "—"}
                        </td>
                        <td className={`${cellClass} truncate text-slate-600 dark:text-slate-300`} title={item?.doctorName}>
                          {item?.doctorName || "—"}
                        </td>
                        <td className={`${cellClass} whitespace-nowrap text-right font-medium text-slate-700 tabular-nums dark:text-slate-200`}>
                          {formatAmount(item?.payable)}
                        </td>
                        <td className={`${cellClass} whitespace-nowrap text-right font-semibold text-orange-600 tabular-nums dark:text-orange-400`}>
                          {formatAmount(item?.discount)}
                        </td>
                        <td className={`${cellClass} whitespace-nowrap text-right font-semibold text-emerald-600 tabular-nums dark:text-emerald-400`}>
                          {formatAmount(item?.cash)}
                        </td>
                        <td className={`${cellClass} whitespace-nowrap text-right font-semibold text-indigo-600 tabular-nums dark:text-indigo-400`}>
                          {formatAmount(item?.card)}
                        </td>
                        <td className={`${cellClass} whitespace-nowrap text-right font-semibold text-sky-600 tabular-nums dark:text-sky-400`}>
                          {formatAmount(item?.bankTransfer)}
                        </td>
                        <td className={`${cellClass} whitespace-nowrap text-right font-semibold text-emerald-700 tabular-nums dark:text-emerald-300`}>
                          {formatAmount(paidTotal)}
                        </td>
                        <td
                          className={[
                            cellClass,
                            "whitespace-nowrap text-right font-semibold tabular-nums",
                            isDue
                              ? "text-red-600 dark:text-red-400"
                              : "text-emerald-600 dark:text-emerald-400",
                          ].join(" ")}
                        >
                          {isDue ? "Due " : "Adv "}
                          {formatAmount(dueAmount)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="mt-3 space-y-2 md:hidden">
            {displayedPayments.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 px-3 py-5 text-center text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400">
                No payments found.
              </div>
            ) : (
              displayedPayments.map((item: any, index) => {
                const paidTotal = getPaidTotal(item);
                const dueAmount = toAmount(item?.due);
                const isDue = dueAmount > 0;

                return (
                  <div
                    key={item?._id ?? item?.treatmentId ?? index}
                    onClick={() => navigate(`/client/payments/invoice/${item?.treatmentId}`)}
                    className="cursor-pointer rounded-lg border border-slate-200 bg-slate-50 p-2.5 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800/80 dark:hover:bg-slate-700/80"
                  >
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-slate-900 dark:text-slate-100">
                          {item?.patientName || "—"}
                        </p>
                        <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                          {item?.doctorName || "—"}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                          #{item?.invoiceNo ?? "—"}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          {paymentRowDate(item)}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 text-[11px] sm:grid-cols-4">
                      <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                        <p className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">{formatAmount(item?.payable)}</p>
                      </div>
                      <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                        <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Paid</p>
                        <p className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">{formatAmount(paidTotal)}</p>
                      </div>
                      <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                        <p className="text-[10px] text-orange-600 dark:text-orange-400">Discount</p>
                        <p className="font-semibold tabular-nums text-orange-700 dark:text-orange-300">{formatAmount(item?.discount)}</p>
                      </div>
                      <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                        <p className={`text-[10px] ${isDue ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                          {isDue ? "Due" : "Adv"}
                        </p>
                        <p className={`font-semibold tabular-nums ${isDue ? "text-red-700 dark:text-red-300" : "text-emerald-700 dark:text-emerald-300"}`}>
                          {formatAmount(dueAmount)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {dateRangeMode !== "today" && !filtersLoadAllRows ? (
            <div className="mt-3 border-t border-slate-100 pt-2.5 dark:border-slate-700">
              <BottomTab title="Payment" count={count} onPageChange={onPageChange} />
            </div>
          ) : null}
          {report && <AllPaymentReport closeModal={closeModal} />}
        </div>
      </div>
    </CommonLayout>
  );
}

export default AllPaymentList;
